if (!customElements.get('dl-page-sms-subscribe-y')) {
  class DLPageSmsSubscribeY extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.form = this.querySelector('[data-phone-form]');
      this.phoneRow = this.querySelector('[data-phone-row]');
      this.phoneInput = this.querySelector('[data-phone-input]');
      this.phoneError = this.querySelector('[data-phone-error]');
      this.submitError = this.querySelector('[data-submit-error]');
      this.submitButton = this.querySelector('[data-submit-button]');
      this.submitLabel = this.querySelector('[data-submit-label]');
      this.defaultSubmitLabel = this.submitLabel?.textContent?.trim() || '';
      this.loadingLabel = this.dataset.loadingLabel || 'Submitting…';
      this.email = this.getEmailFromUrl();
      this.currentState = 'eligible';
      this.onSubmit = this.submit.bind(this);
      this.onPhoneInput = this.clearErrors.bind(this);

      this.form?.addEventListener('submit', this.onSubmit);
      this.phoneInput?.addEventListener('input', this.onPhoneInput);
      this.buildConfetti();
      this.setInitialState();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.phoneInput?.removeEventListener('input', this.onPhoneInput);
      this._initialized = false;
    }

    getEmailFromUrl() {
      const paramName = (this.dataset.emailQueryParam || 'email').trim() || 'email';
      const params = new URLSearchParams(window.location.search);
      // URLSearchParams 将未编码的 "+" 解成空格；Braze 链接仍应使用 url_encode。
      const fromUrl = String(params.get(paramName) || '').replace(/ /g, '+').trim();
      const previewEmail = String(this.dataset.previewEmail || '').trim();
      const email = fromUrl || previewEmail;
      return this.isValidEmail(email) ? email : '';
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
    }

    setInitialState() {
      const requestedState = new URLSearchParams(window.location.search).get('state');
      if (requestedState === 'success' || requestedState === 'already') {
        this.setState(requestedState);
        return;
      }
      this.setState('eligible');
      if (requestedState === 'error') {
        this.showSubmitError(this.dataset.submitError);
      }
    }

    setState(state) {
      this.currentState = state;
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        const active = panel.dataset.panel === state;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      if (state === 'success') {
        const heading = this.querySelector('[data-success-title]');
        requestAnimationFrame(() => heading?.focus({ preventScroll: true }));
      }
    }

    setLoading(loading) {
      if (this.submitButton) {
        this.submitButton.disabled = loading;
        this.submitButton.classList.toggle('is-loading', loading);
        this.submitButton.setAttribute('aria-busy', String(loading));
      }
      if (this.submitLabel) {
        this.submitLabel.textContent = loading ? this.loadingLabel : this.defaultSubmitLabel;
      }
    }

    clearErrors() {
      this.phoneRow?.classList.remove('has-error');
      this.phoneError?.classList.remove('is-visible');
      this.submitError?.classList.remove('is-visible');
      this.phoneInput?.setAttribute('aria-invalid', 'false');
    }

    showPhoneError() {
      this.phoneRow?.classList.add('has-error');
      this.phoneError?.classList.add('is-visible');
      this.phoneInput?.setAttribute('aria-invalid', 'true');
      this.phoneInput?.focus();
    }

    showSubmitError(message) {
      if (!this.submitError) return;
      this.submitError.textContent = message || this.dataset.submitError || 'Unable to subscribe. Please try again.';
      this.submitError.classList.add('is-visible');
    }

    normalizePhone(rawPhone) {
      const countryDigits = String(this.dataset.countryCode || '+44').replace(/\D/g, '') || '44';
      let digits = String(rawPhone || '').replace(/\D/g, '');
      if (!digits) return '';

      const enteredInternational = String(rawPhone || '').trim().startsWith('+');
      if (enteredInternational && digits.startsWith(countryDigits)) {
        digits = digits.slice(countryDigits.length);
      } else if (!enteredInternational && digits.startsWith(countryDigits) && digits.length > countryDigits.length + 6) {
        digits = digits.slice(countryDigits.length);
      }

      if (countryDigits === '44') {
        digits = digits.replace(/^0+/, '');
        if (!/^[1-9]\d{8,10}$/.test(digits)) return '';
      } else if (countryDigits === '1') {
        if (digits.length !== 10 || /^[01]/.test(digits)) return '';
      } else if (digits.length < 7) {
        return '';
      }

      const fullDigits = `${countryDigits}${digits}`;
      if (fullDigits.length < 8 || fullDigits.length > 15 || /^(\d)\1+$/.test(digits)) return '';
      return `+${fullDigits}`;
    }

    async waitForBraze() {
      const pixel = window.DLPixelH;
      if (!pixel || typeof pixel.whenBrazeReady !== 'function') {
        throw new Error('DLPixelH Braze integration is unavailable');
      }
      await pixel.whenBrazeReady(() => true, 15000);
    }

    async subscribe(email, phone) {
      await this.waitForBraze();
      if (email) {
        await window.DLPixelH.identify(email);
      }

      const braze = window.braze;
      const user = braze?.getUser?.();
      if (!user) throw new Error('Braze user is unavailable');

      // 有 Email 时绑定到对应用户；没有 Email 时保留当前 Braze 匿名用户，允许仅手机号订阅。
      if (email) {
        user.setEmail?.(email);
        user.addAlias?.(email, 'user_email');
      }
      user.addAlias?.(phone, 'user_phone');

      if (typeof user.setPhoneNumber !== 'function' || user.setPhoneNumber(phone) === false) {
        throw new Error('Braze rejected the phone number');
      }

      const groupId = String(this.dataset.smsGroupId || '').trim();
      if (!groupId || typeof user.addToSubscriptionGroup !== 'function') {
        throw new Error('SMS subscription group is unavailable');
      }
      if (user.addToSubscriptionGroup(groupId) === false) {
        throw new Error('Braze rejected the SMS subscription');
      }

      const subscribedAt = new Date().toISOString();
      await window.DLPixelH.setUserProperty('sms_subscription_received', true);
      await window.DLPixelH.setUserProperty('sms_subscription_date', subscribedAt);

      await this.capture(this.dataset.submitEventName || 'subscribe_submit_sms', {
        channel: 'sms',
        email_attached: Boolean(email),
        subscribed_at: subscribedAt
      });

      braze.requestImmediateDataFlush?.();
    }

    async submit(event) {
      event.preventDefault();
      if (this.currentState === 'loading') return;
      this.clearErrors();

      const phone = this.normalizePhone(this.phoneInput?.value);
      if (!phone) {
        this.showPhoneError();
        return;
      }

      this.currentState = 'loading';
      this.setLoading(true);
      try {
        await this.subscribe(this.email, phone);
        this.setLoading(false);
        this.setState('success');
      } catch (error) {
        console.error('[DLPageSmsSubscribeY] SMS subscription failed', error);
        this.currentState = 'eligible';
        this.setLoading(false);
        this.showSubmitError(this.dataset.submitError);
        this.phoneInput?.focus();
      }
    }

    capture(eventName, eventData) {
      if (!eventName || typeof window.DLPixelH?.capture !== 'function') {
        return Promise.resolve();
      }
      return window.DLPixelH.capture(eventName, {
        section_id: this.dataset.sectionId,
        ...eventData
      });
    }

    buildConfetti() {
      const host = this.querySelector('[data-confetti]');
      if (!host || host.children.length) return;
      const colors = ['#f57474', '#67cbab', '#f5c574'];
      const spots = [
        [7, 44, -48], [14, 16, -98], [23, 58, -48], [37, 12, 0], [45, 34, -98],
        [56, 20, -48], [65, 50, 34], [75, 8, 0], [85, 36, 53], [92, 68, -20]
      ];
      spots.forEach((spot, index) => {
        const piece = document.createElement('span');
        piece.style.setProperty('--confetti-left', `${spot[0]}%`);
        piece.style.setProperty('--confetti-top', `${spot[1]}px`);
        piece.style.setProperty('--confetti-rotation', `${spot[2]}deg`);
        piece.style.setProperty('--confetti-color', colors[index % colors.length]);
        host.append(piece);
      });
    }
  }

  customElements.define('dl-page-sms-subscribe-y', DLPageSmsSubscribeY);
}
